import { doctorNameEncoder, errorHandling, URL_QUERY, getImageUrlWithSize } from '@common/Utility/common-utils';
import { TELECONSULT_ROUTE } from '@common/Utility/constants';
import { getMyDoctorList } from 'aster-foundation/core/teleconsultant/doctor/redux/actions';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IC_BOOKMARK_BLUE_CIRCLE,
  IC_DOCTOR,
  IC_INPERSON_GREEN,
  IC_VIDEO_CONSULT_CIRCLE,
} from '../../../../public/images';
import SectionBox from '../SectionBox/SectionBox';
import SlickSlider from '../SlickSlider/SlickSlider';
import { MyDoctorStyled } from './MyDoctorStyled';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const responsive = [
  {
    breakpoint: 980,
    settings: {
      slidesToShow: 6.3,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToShow: 5.3,
    },
  },
  {
    breakpoint: 480,
    settings: {
      variableWidth: true,
      slidesToShow: 2.5,
      infinite: false,
    },
  },
];

const imageToTypeMap = {
  'In-Person': IC_INPERSON_GREEN,
  Online: IC_VIDEO_CONSULT_CIRCLE,
  Bookmark: IC_BOOKMARK_BLUE_CIRCLE,
};

const DoctorCard = ({ data }) => {
  const router = useRouter();
  const generateURL = useNavigationURL();

  const splitName = data?.doctorName?.split(' ');

  const trimmedName =
    splitName?.[1]?.length > 7
      ? `${splitName[0]} ${splitName[1]?.slice(0, 7)}...`
      : `${splitName[0]} ${splitName[1]} ${splitName[2]?.[0] ? `${splitName[2]?.[0]}.` : ''}`;

  const navigateToDoctorDetails = () => {
    sessionStorage.setItem('patientId', data?.patientId);
    if (data?.active) {
      router.push({
        pathname: generateURL(`${TELECONSULT_ROUTE}/d/${doctorNameEncoder(data?.doctorName)?.replace(URL_QUERY, '')}/${
          data?.doctorId
        }`),
      });
    } else {
      router.push({
        pathname: generateURL(`${TELECONSULT_ROUTE}/s/${(data?.speciality)?.trim()?.toLowerCase()?.replace(/\s+/g, '-')}`),
        query: { isMyDoctors: true, doctorName: `${doctorNameEncoder(data?.doctorName)}`}
      });
    }
  };

  const getHref = () => {
    let href = '';
    if(data?.active) {
       href = generateURL(`${TELECONSULT_ROUTE}/d/${doctorNameEncoder(data?.doctorName)?.replace(URL_QUERY, '')}/${
        data?.doctorId
      }`);
    } else {
      href = generateURL(`${TELECONSULT_ROUTE}/s/${(data?.speciality)?.trim()?.toLowerCase()?.replace(/\s+/g, '-')}?isMyDoctors=true&doctorName=${doctorNameEncoder(data?.doctorName)}`)
    }
    return href;
  };

  return (
    <Link href={getHref()} passHref>
      <a
        data-testid="onclicksecondtest"
        className='myDoctorsHome'
        onClick={() => navigateToDoctorDetails()}
      >
      <div className="doctorCard">
        <div className="doctorCard__img">
          <Image src={getImageUrlWithSize(data?.doctorImageUrlWeb, 68, 68) || IC_DOCTOR.src} alt={data?.doctorName} width={68} height={68} unoptimized/>
          <div className="doctorCard__img__overlay">
            <Image
              src={imageToTypeMap[data?.visitType]}
              alt={data?.visitType}
              width={24}
              height={24}
              className={data?.visitType === 'Bookmark' ? 'bookmark_ic' : ''}
            />
          </div>
        </div>
        <div className="doctorCard__content">
          <h3>{trimmedName}</h3>
          <p>{data?.speciality}</p>
        </div>
      </div>
      </a>
    </Link>
  );
};

DoctorCard.propTypes = {
  data: PropTypes.shape({
    doctorImageUrlWeb: PropTypes.string,
    doctorName: PropTypes.string,
    visitType: PropTypes.string,
    speciality: PropTypes.string,
    doctorId: PropTypes.string,
    patientId: PropTypes.string,
  }).isRequired,
};

const MyDoctor = (props) => {
  const dispatch = useDispatch();

  const [doctorData, setDoctorData] = useState([]);
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);

  const userLoginMemoised = useMemo(() => userLogin, [userLogin?.id]);

  useEffect(() => {
    if (userLoginMemoised?.id) {
      dispatch(
        getMyDoctorList({
          onSuccess: (response) => {
            setDoctorData(response?.data?.doctorList);
          },
          onFailure: (error) => {
            errorHandling(error);
          },
        }),
      );
    }
  }, [userLoginMemoised]);

  return !!doctorData?.length && !props?.isGuest ? (
    <MyDoctorStyled>
      <SectionBox customClass="sectionBox" title={props?.title} customOuterClass="whiteBg">
        <SlickSlider
          sliderSettings={{
            slidesToShow: 9.3,
            infinite: false,
            className: 'myDoctorSlider',
            dots: false,
            responsive,
          }}
        >
          {doctorData?.map((item) => (
            <DoctorCard key={item?.doctorId} data={item} />
          ))}
        </SlickSlider>
      </SectionBox>
    </MyDoctorStyled>
  ) : null;
};

MyDoctor.propTypes = {
  title: PropTypes.string.isRequired,
  isGuest: PropTypes.bool.isRequired,
};

export default MyDoctor;
